/**
 * The photo viewer, shared by the scoring stage and the public pages.
 *
 * It renders thumbnails — never full-size files — because a strip of a dozen target
 * photos should cost a phone on range wifi a few hundred kilobytes. The full file is one
 * deliberate tap away via a plain link; nothing fetches it on the strip's behalf.
 *
 * The first image of a primary-first list leads; the lead is tracked by id, so a promote
 * that re-orders the parent's freshly-refetched list keeps showing the same photo.
 */

import { useState } from "react";
import type { TargetImage } from "./types";
import { formatBytes, imageUrl, thumbUrl } from "./images";
import { Badge, Button, ErrorNote, classNames } from "./components";

export function PhotoViewer({
  images,
  title,
  onClose,
  onPromote,
  initialLead = null,
}: {
  images: TargetImage[];
  title: string;
  onClose: () => void;
  /** Passed only where the viewer is a scorer: promoting belongs to the person who owns
   *  the card, not to whoever is looking. */
  onPromote?: (imageId: string) => Promise<void>;
  /** The thumbnail the viewer was opened from; null lands on the list's lead, which is
   *  the primary because the server sorts it first. */
  initialLead?: string | null;
}) {
  const [leadId, setLeadId] = useState<string | null>(initialLead);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  if (images.length === 0) return null;
  const index = Math.max(
    images.findIndex((image) => image.id === leadId),
    0,
  );
  const lead = images[index];

  const promote = async () => {
    if (onPromote === undefined) return;
    setBusyId(lead.id);
    setError(null);
    try {
      await onPromote(lead.id);
    } catch (cause) {
      setError(cause);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="gallery" role="dialog" aria-label={`Photos: ${title}`}>
      <div className="gallery-bar">
        <strong>{title}</strong>
        <span className="spacer" />
        <span className="muted">{images.length === 1 ? "1 photo" : `${images.length} photos`}</span>
        <Button onClick={onClose}>Close</Button>
      </div>
      {error !== null ? <ErrorNote error={error} onDismiss={() => setError(null)} /> : null}
      <div className="gallery-lead">
        <img src={thumbUrl(lead.id)} alt={title} />
        <div className="gallery-caption">
          <span>
            {lead.width}×{lead.height} · {formatBytes(lead.bytes)}
          </span>
          {lead.is_primary ? <Badge tone="good">Primary</Badge> : null}
          <a className="btn btn-ghost" href={imageUrl(lead.id)}>
            Open full photo
          </a>
          {onPromote !== undefined && !lead.is_primary ? (
            <Button busy={busyId === lead.id} onClick={promote}>
              Make primary
            </Button>
          ) : null}
        </div>
      </div>
      {images.length > 1 ? (
        <div className="gallery-strip">
          {images.map((image, at) => (
            <button
              key={image.id}
              type="button"
              className={classNames("gallery-thumb", at === index ? "on" : false)}
              onClick={() => setLeadId(image.id)}
              aria-label={image.is_primary ? "Primary photo" : `Photo ${String(at + 1)}`}
            >
              <img src={thumbUrl(image.id)} alt="" loading="lazy" />
              {image.is_primary ? <span className="gallery-star" aria-hidden="true">★</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
